'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useConvex } from 'convex/react';
import toast from 'react-hot-toast';
import { showToast } from '../lib/toast';
import { getErrorMessage, getErrorType } from '../lib/errorMessages';
import { validateForm } from '../lib/validation';
import { isValidSession } from '../lib/auth';
import { useUser } from '../contexts/UserContext';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Code, 
  Zap, 
  Shield, 
  Users, 
  ArrowRight,
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Sun,
  Moon
} from 'lucide-react';

interface User {
  _id: Id<"users">;
  email: string;
  name?: string;
  role: 'user' | 'admin' | 'super_admin' | 'developer';
  daily_credits: number;
  permanent_credits: number;
  total_credits: number;
}

export default function HomePage() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const router = useRouter();
  const convex = useConvex();
  const { theme, toggleTheme } = useTheme();
  const { user, setUser, isLoading } = useUser();

  useEffect(() => {
    // If user is logged in (from UserContext), redirect to dashboard
    if (!isLoading && user) {
      router.push('/dashboard');
    }
  }, [user, isLoading, router]);

  const handleAuth = async (e: React.FormEvent, mode?: 'login' | 'signup') => {
    e.preventDefault();
    
    // Use explicit mode parameter if provided, otherwise fall back to isLogin state
    const authMode = mode || (isLogin ? 'login' : 'signup');
    
    if (loading) return; // Prevent double submission
    
    // Client-side validation
    const validationError = validateForm({
      email,
      password,
      name,
      isSignup: authMode === 'signup'
    });
    
    if (validationError) {
      showToast.error(validationError);
      return;
    }

    setLoading(true);
    
    // Show loading toast
    const loadingToast = showToast.loading(
      authMode === 'login' ? 'Signing in...' : 'Creating account...'
    );
    
    try {
      if (authMode === 'login') {
        // Login
        const result = await convex.mutation(api.users.loginUser, {
          email,
          password,
        });
        
        // Set user in context (this will handle localStorage automatically)
        setUser(result.user as any);
        toast.dismiss(loadingToast);
        
        // Show brief success message and redirect
        showToast.success('Welcome back!', 1200);
        
        // Dismiss all toasts and redirect after a brief moment
        setTimeout(() => {
          toast.dismiss();
          router.push('/dashboard');
        }, 800);
      } else {
        // Register
        await convex.mutation(api.users.registerUser, {
          email,
          password,
          name,
        });
        
        // Send welcome email (don't fail registration if email fails)
        try {
          await fetch('/api/send-welcome-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userEmail: email,
              userName: name,
            }),
          });
          console.log('Welcome email sent successfully');
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError);
          // Continue with registration flow even if email fails
        }
        
        // Small delay to ensure proper state management
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Show success message and switch to login
        toast.dismiss(loadingToast);
        showToast.success('Account created successfully! Please check your email for a welcome message and sign in with your credentials.');
        closeModal();
        setShowLoginModal(true);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Auth error:', error);
      
      const userFriendlyMessage = getErrorMessage(error);
      const errorType = getErrorType(error);
      
      // Handle specific error types with different actions
      if (authMode === 'signup' && errorType === 'auth' && 
          (error as any)?.message?.includes('User already exists')) {
        showToast.error('Account already exists. Please sign in instead.');
        closeModal();
        setShowLoginModal(true);
      } else {
        showToast.error(userFriendlyMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowLoginModal(false);
    setShowSignupModal(false);
    setEmail('');
    setPassword('');
    setName('');
    setShowPassword(false);
  };

  // Show loading state while UserContext is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="glass-card text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300 mx-auto mb-4"></div>
          <p className="text-gray-700 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is logged in, show redirecting state
  if (user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="glass-card text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300 mx-auto mb-4"></div>
          <p className="text-gray-700 dark:text-gray-300">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      
      {/* Header */}
      <header className="relative z-10">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-800 dark:bg-gray-200 rounded-xl flex items-center justify-center">
                <span className="text-white dark:text-gray-900 font-bold text-lg">D</span>
              </div>
              <div>
                <h1 className="text-gray-900 dark:text-white font-bold text-xl">DigiPin</h1>
                <p className="text-gray-600 dark:text-gray-400 text-xs">Developer Portal</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg"
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? (
                  <Sun className="w-6 h-6" />
                ) : (
                  <Moon className="w-6 h-6" />
                )}
              </button>
              
              <button
                onClick={() => setShowLoginModal(true)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Login
              </button>
              <button
                onClick={() => setShowSignupModal(true)}
                className="glass-button"
              >
                Sign Up
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center">
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
              Privacy-First
              <span className="block text-gray-700 dark:text-gray-300">
                Location Encoding
              </span>
            </h1>
            
            <p className="text-base sm:text-xl text-gray-600 dark:text-gray-400 mb-8 sm:mb-12 max-w-3xl mx-auto px-2">
              Convert coordinates to DigiPins and back with our secure API. 
              Perfect for location-based apps that need privacy protection.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6 px-4">
              <button
                onClick={() => setShowSignupModal(true)}
                className="glass-button text-sm sm:text-lg px-6 py-3 sm:px-8 sm:py-4 w-full sm:w-auto"
              >
                <span>Get Started Free</span>
              </button>
              
              <button
                onClick={() => setShowLoginModal(true)}
                className="glass-button bg-gray-100 dark:bg-gray-800 text-sm sm:text-lg px-6 py-3 sm:px-8 sm:py-4 w-full sm:w-auto"
              >
                Access Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Code className="w-8 h-8 text-gray-600 dark:text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Simple API</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Convert lat/lng to DigiPin with a single API call. RESTful endpoints with comprehensive documentation.
              </p>
            </div>
            
            <div className="glass-card text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-gray-600 dark:text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Privacy First</h3>
              <p className="text-gray-600 dark:text-gray-400">
                DigiPin algorithm ensures location privacy while maintaining usability for your applications.
              </p>
            </div>
            
            <div className="glass-card text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-gray-600 dark:text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Fast & Reliable</h3>
              <p className="text-gray-600 dark:text-gray-400">
                High-performance API with 99.9% uptime. Built on serverless infrastructure for scale.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="glass-card max-w-md w-full">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 text-center">Welcome Back</h3>
            
            <form onSubmit={(e) => handleAuth(e, 'login')} className="space-y-4">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-6 h-6" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="glass-input pl-12 w-full"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-6 h-6" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="glass-input pl-12 pr-12 w-full"
                    placeholder="Your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading || !email || !password}
                className="glass-button w-full"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  closeModal();
                  setShowSignupModal(true);
                }}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm"
              >
                Don't have an account? Sign up
              </button>
            </div>
            
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="glass-card max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 text-center">Create Account</h3>
            
            <form onSubmit={(e) => handleAuth(e, 'signup')} className="space-y-4">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-6 h-6" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="glass-input pl-12 w-full"
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-6 h-6" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="glass-input pl-12 w-full"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-6 h-6" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="glass-input pl-12 pr-12 w-full"
                    placeholder="Create a password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Minimum 6 characters</p>
              </div>
              
              <button
                type="submit"
                disabled={loading || !email || !password || !name}
                className="glass-button w-full"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  closeModal();
                  setShowLoginModal(true);
                }}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm"
              >
                Already have an account? Sign in
              </button>
            </div>
            
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
